import React, { FC } from "react";
import { Box } from "@radix-ui/themes";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import { controlFlowModule } from "@/modules/control-flow";
import { SidebarItemKind, SidebarButtonItem } from "@/app/layout/types";
import { SIDEBAR_ITEM_HEIGHT } from "@/app/layout/constants";

interface SidebarItemRowProps {
  item: SidebarButtonItem;
  onSelect?: () => void;
  indentLevel?: number;
}

const SidebarButtonItemRow: FC<{
  item: SidebarButtonItem;
  onSelect?: () => void;
  indentLevel: number;
}> = ({ item, onSelect, indentLevel }) => {
  const handleClick = () => {
    // Call onSelect if provided (e.g., for closing mobile sidebar)
    onSelect?.();
  };
  return (
    <SidebarItemRowBox>
      <SidebarItemRowLink
        to={item.href}
        onClick={handleClick}
        isActive={item.isActive}
        indentLevel={indentLevel}
      >
        {item.icon && <item.icon size={16} />}
        <span>{item.label}</span>
      </SidebarItemRowLink>
    </SidebarItemRowBox>
  );
};

export const SidebarItemRow: FC<SidebarItemRowProps> = ({ item, onSelect, indentLevel = 0 }) => {
  if (item.kind === SidebarItemKind.Button) {
    return <SidebarButtonItemRow item={item} onSelect={onSelect} indentLevel={indentLevel} />;
  }

  controlFlowModule.assertUnreachableCase({
    message: "Failed to match sidebar item kind.",
    value: item.kind,
  });

  return null as never;
};

const SidebarItemRowBox = styled(Box)`
  height: ${SIDEBAR_ITEM_HEIGHT}px;
  width: 100%;
`;

const SidebarItemRowLink = styled(Link, {
  shouldForwardProp: prop => prop !== "isActive" && prop !== "indentLevel",
})<{ isActive: boolean; indentLevel: number }>`
  display: flex;
  align-items: center;
  text-decoration: none;
  width: 100%;
  font-size: 12px;
  border: none !important;
  border-radius: 4px !important;
  box-shadow: none !important;
  height: ${SIDEBAR_ITEM_HEIGHT}px;
  min-height: ${SIDEBAR_ITEM_HEIGHT}px;
  margin: 0px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  padding-left: ${({ indentLevel }) => `${6 + indentLevel * 12}px`} !important;
  padding-right: 0px !important;
  gap: 8px;
  justify-content: flex-start;
  font-weight: var(--font-weight-normal);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--gray-12);
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  position: relative;
  overflow: hidden;
  cursor: pointer;

  background-color: ${({ isActive }) => (isActive ? "var(--gray-4)" : "transparent")};

  &:hover {
    background-color: var(--gray-4);
    color: var(--gray-12);
  }

  &:active {
    background-color: var(--gray-4);
  }
`;
